import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { App, AppState } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { BehaviorSubject, firstValueFrom, Subscription, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  IOpenUsageSessionRequest,
  IUsageSession,
  IUsageSessionHeartbeatResponse,
  UsageSessionChannel,
  UsageSessionEndReason,
  UsageSessionHeartbeatState,
  UsageSessionKind,
} from '../../shared/models/be/usage-session';
import { isAccessTokenValid } from '../../shared/utils/check.utils';
import { HardwareService } from './hardware.service';
import { RequestsService } from './utils/requests.service';
import { LoggerService } from './utils/logger.service';
import { createUuid } from '../../shared/utils/uuid.util';

const APPLICATION_HEARTBEAT_INTERVAL_MS = 30_000;
const STREAMING_HEARTBEAT_INTERVAL_MS = 15_000;
const REQUEST_TIMEOUT_MS = 8_000;
const END_REQUEST_TIMEOUT_MS = 3_000;

interface LocalUsageSession {
  handle: string;
  ownerUserId: number;
  clientSessionId: string;
  kind: UsageSessionKind;
  subjectType?: string;
  subjectId?: string;
  metadata?: Record<string, unknown>;
  serverId?: string;
  lastSequence: number;
  desiredState: UsageSessionHeartbeatState;
  ended: boolean;
  heartbeatQueued: boolean;
  heartbeatTimer?: ReturnType<typeof setInterval>;
  operationQueue: Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class UsageTrackingService implements OnDestroy {
  private readonly endpoint = `${environment.apiEndpoint}/usage/sessions`;
  private readonly sessions = new Map<string, LocalUsageSession>();

  private applicationHandle?: string;
  private initialized = false;
  private foreground = typeof document === 'undefined' || document.visibilityState !== 'hidden';
  private readonly foregroundSubject = new BehaviorSubject<boolean>(this.foreground);
  private currentUserSubscription?: Subscription;
  private nativeAppStateListener?: PluginListenerHandle;

  private readonly visibilityChangeHandler = () => {
    this.setForeground(document.visibilityState === 'visible');
  };

  private readonly pageHideHandler = () => {
    this.endAllWithKeepalive();
  };

  private readonly pageShowHandler = () => {
    this.setForeground(true);
    this.onAuthenticationAvailable();
  };

  constructor(
    private http: HttpClient,
    private requestsService: RequestsService,
    private hardwareService: HardwareService,
    private loggerService: LoggerService
  ) {}

  readonly foreground$ = this.foregroundSubject.asObservable();

  isInForeground(): boolean {
    return this.foreground;
  }

  /** Initializes browser/native lifecycle tracking once for the whole application. */
  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
    window.addEventListener('pageshow', this.pageShowHandler);

    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', (state: AppState) => {
        this.setForeground(state.isActive);
      }).then((listener) => {
        this.nativeAppStateListener = listener;
      });
    }

    this.currentUserSubscription = this.requestsService.currentUser$.subscribe((user) => {
      if (!user?.id) {
        this.discardAllSessions();
        return;
      }

      const applicationSession = this.getApplicationSession();
      if (applicationSession && applicationSession.ownerUserId !== user.id) {
        this.discardAllSessions();
      }

      this.onAuthenticationAvailable();
    });

  }

  /** Called after login or token refresh to ensure this client has its own app session. */
  onAuthenticationAvailable(): void {
    if (!this.initialized || !this.foreground || !isAccessTokenValid()) {
      return;
    }

    const session = this.ensureApplicationSession();
    if (session) {
      this.transitionSession(session, 'active');
    }
  }

  /** Forces a retry as soon as connectivity returns instead of waiting for the next interval. */
  onNetworkAvailable(): void {
    this.onAuthenticationAvailable();
    for (const session of this.sessions.values()) {
      if (!session.ended && session.desiredState === 'active') {
        this.requestHeartbeat(session);
      }
    }
  }

  /**
   * Starts one independent streaming usage session and returns its local handle.
   * Every invocation intentionally creates a distinct clientSessionId.
   */
  beginStreamingSession(deviceId: string, metadata?: Record<string, unknown>): string | undefined {
    if (!deviceId || !this.requestsService.getCurrentUser() || !isAccessTokenValid()) {
      return undefined;
    }

    const session = this.createLocalSession('streaming_view', {
      subjectType: 'iot_device',
      subjectId: deviceId,
      metadata,
    });

    return session?.handle;
  }

  endStreamingSession(handle: string | undefined, reason: UsageSessionEndReason): Promise<void> {
    if (!handle) {
      return Promise.resolve();
    }
    return this.endSession(handle, reason);
  }

  /** Ends only the sessions owned by this frontend instance before invalidating auth. */
  async prepareForLogout(): Promise<void> {
    const streamingHandles = [...this.sessions.values()]
      .filter((session) => session.kind === 'streaming_view')
      .map((session) => session.handle);

    await Promise.all(streamingHandles.map((handle) => this.endSession(handle, 'stream_stopped')));

    if (this.applicationHandle) {
      await this.endSession(this.applicationHandle, 'logout');
    }
  }

  /** Drops local state when authentication is already unusable; backend timeout remains authoritative. */
  discardAllSessions(): void {
    for (const session of this.sessions.values()) {
      this.clearHeartbeatTimer(session);
      session.ended = true;
    }
    this.sessions.clear();
    this.applicationHandle = undefined;
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    window.removeEventListener('pagehide', this.pageHideHandler);
    window.removeEventListener('pageshow', this.pageShowHandler);
    this.currentUserSubscription?.unsubscribe();
    void this.nativeAppStateListener?.remove();
    this.discardAllSessions();
  }

  private ensureApplicationSession(): LocalUsageSession | undefined {
    const existing = this.getApplicationSession();
    if (existing && !existing.ended) {
      return existing;
    }

    const session = this.createLocalSession('application_usage');
    this.applicationHandle = session?.handle;
    return session;
  }

  private getApplicationSession(): LocalUsageSession | undefined {
    return this.applicationHandle ? this.sessions.get(this.applicationHandle) : undefined;
  }

  private createLocalSession(
    kind: UsageSessionKind,
    options: {
      subjectType?: string;
      subjectId?: string;
      metadata?: Record<string, unknown>;
    } = {}
  ): LocalUsageSession | undefined {
    const currentUser = this.requestsService.getCurrentUser();
    if (!currentUser?.id) {
      return undefined;
    }

    const handle = createUuid();
    const session: LocalUsageSession = {
      handle,
      ownerUserId: currentUser.id,
      clientSessionId: createUuid(),
      kind,
      subjectType: options.subjectType,
      subjectId: options.subjectId,
      metadata: options.metadata,
      lastSequence: 0,
      desiredState: 'active',
      ended: false,
      heartbeatQueued: false,
      operationQueue: Promise.resolve(),
    };

    this.sessions.set(handle, session);
    this.startHeartbeatTimer(session);
    void this.enqueue(session, () => this.openRemoteSession(session));
    return session;
  }

  private async openRemoteSession(session: LocalUsageSession): Promise<void> {
    if (session.serverId || session.ended) {
      return;
    }

    let parentSessionId: string | undefined;
    if (session.kind === 'streaming_view') {
      const applicationSession = this.ensureApplicationSession();
      if (applicationSession && applicationSession.handle !== session.handle) {
        await applicationSession.operationQueue;
        parentSessionId = applicationSession.serverId;
      }
      if (!parentSessionId || applicationSession?.ended) {
        return;
      }
    }

    // Lifecycle changes can occur while a stream waits for its application session.
    if (session.ended || session.desiredState !== 'active' || !this.foreground) {
      return;
    }

    const payload: IOpenUsageSessionRequest = {
      clientSessionId: session.clientSessionId,
      kind: session.kind,
      clientInstanceId: this.getOrCreateClientInstanceId(),
      channel: this.getChannel(),
      ...(parentSessionId ? { parentSessionId } : {}),
      ...(session.subjectType ? { subjectType: session.subjectType } : {}),
      ...(session.subjectId ? { subjectId: session.subjectId } : {}),
      ...(session.metadata ? { metadata: session.metadata } : {}),
    };

    try {
      const opened = await firstValueFrom(
        this.http.post<IUsageSession>(this.endpoint, payload).pipe(timeout(REQUEST_TIMEOUT_MS))
      );
      session.serverId = opened.id;
      session.lastSequence = Math.max(session.lastSequence, opened.lastSequence);
      this.loggerService.debug(`Usage session ${opened.kind} opened`, { usageSessionId: opened.id });
    } catch (error) {
      this.handleOpenError(session, error);
    }
  }

  private transitionSession(session: LocalUsageSession, state: UsageSessionHeartbeatState): void {
    if (session.ended) {
      return;
    }

    const changed = session.desiredState !== state;
    session.desiredState = state;

    if (state === 'active') {
      this.startHeartbeatTimer(session);
    } else {
      this.clearHeartbeatTimer(session);
    }

    if (changed) {
      this.requestHeartbeat(session);
    }
  }

  private requestHeartbeat(session: LocalUsageSession): void {
    if (session.ended || session.heartbeatQueued) {
      return;
    }

    session.heartbeatQueued = true;
    void this.enqueue(session, async () => {
      try {
        if (!session.serverId) {
          await this.openRemoteSession(session);
        }
        if (!session.serverId || session.ended) {
          return;
        }

        const sequence = session.lastSequence + 1;
        const response = await firstValueFrom(
          this.http.patch<IUsageSessionHeartbeatResponse>(
            `${this.endpoint}/${session.serverId}/heartbeat`,
            { sequence, state: session.desiredState }
          ).pipe(timeout(REQUEST_TIMEOUT_MS))
        );
        if (response.session.state === 'expired' || response.session.state === 'ended') {
          await this.restartRemoteSession(session);
          return;
        }
        session.lastSequence = Math.max(sequence, response.session.lastSequence);
      } catch (error) {
        await this.handleHeartbeatError(session, error);
      } finally {
        session.heartbeatQueued = false;
      }
    });
  }

  private endSession(handle: string, reason: UsageSessionEndReason): Promise<void> {
    const session = this.sessions.get(handle);
    if (!session || session.ended) {
      return Promise.resolve();
    }

    session.ended = true;
    this.clearHeartbeatTimer(session);

    return this.enqueue(session, async () => {
      try {
        if (session.serverId) {
          await firstValueFrom(
            this.http.post<IUsageSession>(`${this.endpoint}/${session.serverId}/end`, { reason }).pipe(
              timeout(END_REQUEST_TIMEOUT_MS)
            )
          );
        }
      } catch (error) {
        this.loggerService.warn(`Unable to end ${session.kind} usage session`, error as object);
      } finally {
        this.removeLocalSession(session);
      }
    });
  }

  private setForeground(isForeground: boolean): void {
    if (this.foreground === isForeground) {
      return;
    }

    this.foreground = isForeground;
    this.foregroundSubject.next(isForeground);
    const applicationSession = this.getApplicationSession();

    if (!isForeground) {
      if (applicationSession) {
        this.transitionSession(applicationSession, 'background');
      }
      for (const session of [...this.sessions.values()]) {
        if (session.kind === 'streaming_view') {
          void this.endSession(session.handle, 'app_background');
        }
      }
      return;
    }

    this.onAuthenticationAvailable();
  }

  private endAllWithKeepalive(): void {
    const token = localStorage.getItem('ACCESS_TOKEN');
    for (const session of [...this.sessions.values()]) {
      session.ended = true;
      this.clearHeartbeatTimer(session);

      if (session.serverId && token && typeof fetch === 'function') {
        const reason: UsageSessionEndReason = session.kind === 'application_usage' ? 'page_closed' : 'stream_stopped';
        void fetch(`${this.endpoint}/${session.serverId}/end`, {
          method: 'POST',
          keepalive: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-App-Version': environment.appVersion,
          },
          body: JSON.stringify({ reason }),
        }).catch(() => undefined);
      }

      this.removeLocalSession(session);
    }
  }

  private startHeartbeatTimer(session: LocalUsageSession): void {
    if (session.heartbeatTimer || session.ended || session.desiredState !== 'active') {
      return;
    }

    const interval = session.kind === 'application_usage'
      ? APPLICATION_HEARTBEAT_INTERVAL_MS
      : STREAMING_HEARTBEAT_INTERVAL_MS;
    session.heartbeatTimer = setInterval(() => this.requestHeartbeat(session), interval);
  }

  private clearHeartbeatTimer(session: LocalUsageSession): void {
    if (session.heartbeatTimer) {
      clearInterval(session.heartbeatTimer);
      session.heartbeatTimer = undefined;
    }
  }

  private enqueue(session: LocalUsageSession, operation: () => Promise<void>): Promise<void> {
    const next = session.operationQueue.then(operation, operation);
    session.operationQueue = next.catch(() => undefined);
    return next;
  }

  private async handleHeartbeatError(session: LocalUsageSession, error: unknown): Promise<void> {
    const status = error instanceof HttpErrorResponse ? error.status : undefined;

    if (status === 404 || status === 409) {
      await this.restartRemoteSession(session);
      return;
    }

    if (status === 401 || status === 403) {
      this.removeLocalSession(session);
      return;
    }

    this.loggerService.warn(`Usage heartbeat failed for ${session.kind}`, error as object);
  }

  private async restartRemoteSession(session: LocalUsageSession): Promise<void> {
    if (session.ended) {
      return;
    }

    session.serverId = undefined;
    session.clientSessionId = createUuid();
    session.lastSequence = 0;

    // Keep the local handle, so the video component can still close the recovered stream.
    // A background application will open its replacement when it returns to the foreground.
    if (session.desiredState === 'active' && this.foreground) {
      await this.openRemoteSession(session);
    }
  }

  private handleOpenError(session: LocalUsageSession, error: unknown): void {
    const status = error instanceof HttpErrorResponse ? error.status : undefined;

    if (status === 409) {
      session.clientSessionId = createUuid();
    } else if (status === 401 || status === 403 || status === 400) {
      this.removeLocalSession(session);
    }

    this.loggerService.warn(`Unable to open ${session.kind} usage session`, error as object);
  }

  private removeLocalSession(session: LocalUsageSession): void {
    session.ended = true;
    this.clearHeartbeatTimer(session);
    if (this.sessions.get(session.handle) === session) {
      this.sessions.delete(session.handle);
    }
    if (this.applicationHandle === session.handle) {
      this.applicationHandle = undefined;
    }
  }

  private getOrCreateClientInstanceId(): string {
    let clientInstanceId = localStorage.getItem('WITA_DEVICE_ID');
    if (!clientInstanceId) {
      clientInstanceId = createUuid();
      localStorage.setItem('WITA_DEVICE_ID', clientInstanceId);
    }
    return clientInstanceId;
  }

  private getChannel(): UsageSessionChannel {
    if (Capacitor.isNativePlatform()) {
      return 'android';
    }
    return 'portal';
  }
}
